import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import {
  loadStyleMaster,
  saveStyleMaster,
  previewStyleMaster,
  bootstrapStyleMaster,
  StyleMaster,
  AnchorDef,
} from '@/api/styleMaster'
import { useProjectPermissions } from '@/auth/useProjectPermissions'
import { ReadOnlyBadge } from '@/auth/ReadOnlyBadge'

type DragMode = 'move' | 'resize-br' | 'resize-tr' | 'resize-bl' | 'resize-tl'

interface DragState {
  name: string
  mode: DragMode
  startMouseX: number
  startMouseY: number
  startAnchorX: number
  startAnchorY: number
  startScale: number
  baseW: number   // intrinsic image width at scale 1
  baseH: number   // intrinsic image height at scale 1
}

const ANCHOR_COLOURS: Record<string, string> = {
  collar:  '#dc2626',
  cuff:    '#0ea5e9',
  pocket:  '#16a34a',
  placket: '#a855f7',
  buttons: '#f59e0b',
}

// Sensible per-attribute fallbacks. Used when an attr key isn't in the
// list of options yet (the master may declare new keys at any time).
const FALLBACK_PREVIEW_ATTRS: Record<string, string> = {
  fabric: 'cotton',
  collar: 'spread',
  cuff: 'single',
  pocket: 'patch',
  placket: 'standard',
  buttons: 'white',
}


function colourFor(attr: string): string {
  if (ANCHOR_COLOURS[attr]) return ANCHOR_COLOURS[attr]
  // Hash the attr name into a stable hue so new attributes still get
  // distinct, readable handle colours.
  let h = 0
  for (let i = 0; i < attr.length; i++) h = (h * 31 + attr.charCodeAt(i)) >>> 0
  return `hsl(${h % 360}, 65%, 45%)`
}


function defaultAttrsFromMaster(m: StyleMaster): Record<string, string> {
  const out: Record<string, string> = {}
  for (const [attr, opts] of Object.entries(m.elements || {})) {
    const keys = Object.keys(opts)
    out[attr] = FALLBACK_PREVIEW_ATTRS[attr] && keys.includes(FALLBACK_PREVIEW_ATTRS[attr])
      ? FALLBACK_PREVIEW_ATTRS[attr]
      : (keys[0] || '')
  }
  // Fabric is a tint, not a stamped element — give it a default if the
  // master declares fabric_swatches but no `fabric` element.
  if (!out.fabric && m.fabric_swatches) {
    out.fabric = FALLBACK_PREVIEW_ATTRS.fabric
  }
  return out
}


function elementImageUrl(
  masterId: string,
  master: StyleMaster,
  attr: string,
  value: string,
): string | null {
  const rel = master.elements?.[attr]?.[value]
  if (!rel) return null
  // Resolve relative paths the same way the backend does — '../' walks
  // up out of the master folder.
  if (rel.startsWith('../')) return `/ecommerce-images/${rel.slice(3)}`
  return `/ecommerce-images/${masterId}/${rel}`
}


function anchorOffset(name: string | undefined, w: number, h: number): [number, number] {
  switch ((name || 'center').toLowerCase()) {
    case 'center': return [w / 2, h / 2]
    case 'top-center': return [w / 2, 0]
    case 'bottom-center': return [w / 2, h]
    case 'top-left': return [0, 0]
    case 'top-right': return [w, 0]
    case 'bottom-left': return [0, h]
    case 'bottom-right': return [w, h]
    default: return [w / 2, h / 2]
  }
}


export default function StylePlacementDesignerPage() {
  const { masterId = 'shirt_master' } = useParams()
  const [master, setMaster] = useState<StyleMaster | null>(null)
  const [savedMaster, setSavedMaster] = useState<StyleMaster | null>(null)
  const [selectedAnchor, setSelectedAnchor] = useState<string | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [previewBusy, setPreviewBusy] = useState(false)
  const [saving, setSaving] = useState(false)
  const { canEditObjectsOrLoading: canEdit } = useProjectPermissions()
  const [bootstrapping, setBootstrapping] = useState(false)
  const [previewColor, setPreviewColor] = useState('blue')
  const [previewAttrs, setPreviewAttrs] = useState<Record<string, string>>({})
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [missing, setMissing] = useState(false)
  // Intrinsic (natural) dimensions of each element PNG, keyed by attr.
  // Populated on image load; used to size the SVG <image> at scale 1.
  const [imgDims, setImgDims] = useState<Record<string, { w: number; h: number }>>({})
  const svgRef = useRef<SVGSVGElement | null>(null)
  const dragRef = useRef<DragState | null>(null)
  const refreshTimer = useRef<number | null>(null)

  const dirty = useMemo(
    () => !!master && !!savedMaster && JSON.stringify(master) !== JSON.stringify(savedMaster),
    [master, savedMaster],
  )

  // Load master on mount
  useEffect(() => {
    let cancelled = false
    setMissing(false)
    setErrorMsg(null)
    setMaster(null)
    setSavedMaster(null)
    loadStyleMaster(masterId)
      .then((m) => {
        if (cancelled) return
        setMaster(m)
        setSavedMaster(JSON.parse(JSON.stringify(m)))
        setPreviewAttrs(defaultAttrsFromMaster(m))
        setSelectedAnchor(Object.keys(m.anchors)[0] || null)
      })
      .catch((err) => {
        if (cancelled) return
        const status = err?.response?.status
        if (status === 404) {
          setMissing(true)
          return
        }
        const msg = err?.response?.data?.detail || err?.message || 'Failed to load master'
        setErrorMsg(String(msg))
      })
    return () => {
      cancelled = true
    }
  }, [masterId])

  // Debounced live preview refresh
  useEffect(() => {
    if (!master) return
    if (refreshTimer.current) {
      window.clearTimeout(refreshTimer.current)
    }
    refreshTimer.current = window.setTimeout(async () => {
      setPreviewBusy(true)
      try {
        const { imageUrl } = await previewStyleMaster(
          masterId,
          previewColor,
          previewAttrs,
          master,
        )
        setPreviewUrl(imageUrl)
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('preview failed', err)
      } finally {
        setPreviewBusy(false)
      }
    }, 300)
    return () => {
      if (refreshTimer.current) window.clearTimeout(refreshTimer.current)
    }
  }, [master, previewColor, previewAttrs, masterId])

  const updateAnchor = useCallback(
    (name: string, patch: Partial<AnchorDef>) => {
      setMaster((m) => {
        if (!m) return m
        return {
          ...m,
          anchors: {
            ...m.anchors,
            [name]: { ...m.anchors[name], ...patch },
          },
        }
      })
    },
    [],
  )

  const clientToSvg = useCallback((clientX: number, clientY: number): [number, number] => {
    const svg = svgRef.current
    if (!svg) return [0, 0]
    const pt = svg.createSVGPoint()
    pt.x = clientX
    pt.y = clientY
    const ctm = svg.getScreenCTM()
    if (!ctm) return [0, 0]
    const inv = ctm.inverse()
    const out = pt.matrixTransform(inv)
    return [out.x, out.y]
  }, [])

  // Generic pointer-down handler: works for drag-to-move on the image
  // body and drag-to-scale on the four corner handles.
  const onElementPointerDown = useCallback(
    (name: string, mode: DragMode, e: React.PointerEvent<SVGElement>) => {
      if (!master) return
      const a = master.anchors[name]
      if (!a) return
      e.preventDefault()
      e.stopPropagation()
      const target = e.target as SVGElement
      try { target.setPointerCapture(e.pointerId) } catch { /* not all elements support capture */ }
      const [sx, sy] = clientToSvg(e.clientX, e.clientY)
      const dims = imgDims[name] || { w: 200, h: 200 }
      dragRef.current = {
        name,
        mode,
        startMouseX: sx,
        startMouseY: sy,
        startAnchorX: a.x,
        startAnchorY: a.y,
        startScale: a.scale ?? 1.0,
        baseW: dims.w,
        baseH: dims.h,
      }
      setSelectedAnchor(name)
    },
    [master, clientToSvg, imgDims],
  )

  const onPointerMove = useCallback(
    (e: React.PointerEvent<SVGSVGElement>) => {
      const drag = dragRef.current
      if (!drag) return
      const [sx, sy] = clientToSvg(e.clientX, e.clientY)
      if (drag.mode === 'move') {
        const nx = Math.round(drag.startAnchorX + (sx - drag.startMouseX))
        const ny = Math.round(drag.startAnchorY + (sy - drag.startMouseY))
        updateAnchor(drag.name, { x: nx, y: ny })
        return
      }
      // Resize: compute new scale by treating the corner-anchor distance
      // as a proxy for the element's bounding diagonal. Distance from
      // the anchor point to the cursor along the corner direction →
      // scale factor relative to the original half-diagonal.
      const dx = sx - drag.startAnchorX
      const dy = sy - drag.startAnchorY
      const startDx = drag.startMouseX - drag.startAnchorX
      const startDy = drag.startMouseY - drag.startAnchorY
      const startMag = Math.hypot(startDx, startDy)
      const nowMag = Math.hypot(dx, dy)
      if (startMag < 1) return
      const ratio = nowMag / startMag
      const next = Math.max(0.05, Math.min(8, drag.startScale * ratio))
      updateAnchor(drag.name, { scale: Number(next.toFixed(3)) })
    },
    [clientToSvg, updateAnchor],
  )

  const onPointerUp = useCallback((_e: React.PointerEvent<SVGSVGElement>) => {
    dragRef.current = null
  }, [])

  const onSave = useCallback(async () => {
    if (!master) return
    setSaving(true)
    try {
      await saveStyleMaster(masterId, master)
      setSavedMaster(JSON.parse(JSON.stringify(master)))
      toast.success('Master saved')
    } catch (err) {
      const e = err as { response?: { data?: { detail?: string } }; message?: string }
      const msg = e?.response?.data?.detail || e?.message || 'Save failed'
      toast.error(String(msg))
    } finally {
      setSaving(false)
    }
  }, [master, masterId])

  const onReset = useCallback(() => {
    if (!savedMaster) return
    setMaster(JSON.parse(JSON.stringify(savedMaster)))
  }, [savedMaster])

  const onBootstrap = useCallback(async () => {
    setBootstrapping(true)
    try {
      const m = await bootstrapStyleMaster(masterId, 'shirt_master')
      setMaster(m)
      setSavedMaster(JSON.parse(JSON.stringify(m)))
      setPreviewAttrs(defaultAttrsFromMaster(m))
      setSelectedAnchor(Object.keys(m.anchors)[0] || null)
      setMissing(false)
      toast.success(`Created master '${masterId}' from shirt_master`)
    } catch (err) {
      const e = err as { response?: { data?: { detail?: string } }; message?: string }
      toast.error(String(e?.response?.data?.detail || e?.message || 'Bootstrap failed'))
    } finally {
      setBootstrapping(false)
    }
  }, [masterId])

  if (errorMsg) {
    return (
      <div style={{ padding: 24 }}>
        <h2>Style Placement Designer</h2>
        <p style={{ color: 'var(--color-error, #dc2626)' }}>Error: {errorMsg}</p>
      </div>
    )
  }

  if (missing) {
    return (
      <div style={{ padding: 24 }}>
        <h2>Style Placement Designer</h2>
        <p>
          No master found for <code>{masterId}</code>. Create a new one by cloning
          the shirt master template — you can then drag/resize the placement of each
          style attribute and Save.
        </p>
        <button
          onClick={onBootstrap}
          disabled={bootstrapping}
          style={{
            padding: '8px 14px',
            background: '#0ea5e9',
            color: 'white',
            border: 'none',
            borderRadius: 6,
            cursor: bootstrapping ? 'not-allowed' : 'pointer',
            marginTop: 12,
          }}
        >
          {bootstrapping ? 'Creating…' : `Create '${masterId}' from shirt_master`}
        </button>
      </div>
    )
  }

  if (!master) {
    return (
      <div style={{ padding: 24 }}>
        <h2>Style Placement Designer</h2>
        <p>Loading master…</p>
      </div>
    )
  }

  const cw = master.canvas.width
  const ch = master.canvas.height
  const basePath = master.base.file.startsWith('../')
    ? `/ecommerce-images/${master.base.file.slice(3)}`
    : `/ecommerce-images/${masterId}/${master.base.file}`

  const anchorEntries = Object.entries(master.anchors).filter(
    ([k]) => !k.startsWith('_'),
  )
  const selectedDef = selectedAnchor ? master.anchors[selectedAnchor] : null

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '320px 1fr 360px',
        gap: 16,
        padding: 16,
        height: 'calc(100vh - 64px)',
        boxSizing: 'border-box',
      }}
    >
      {/* Left: anchor list + numeric editors + sample attr picker */}
      <div style={{ overflow: 'auto' }}>
        <h2 style={{ marginTop: 0 }}>Placement designer</h2>
        <div style={{ fontSize: 14, opacity: 0.7, marginBottom: 16 }}>
          Master: <code>{masterId}</code>
        </div>

        <div style={{ marginBottom: 16 }}>
          {canEdit ? (
            <button
              onClick={onSave}
              disabled={!dirty || saving}
              style={{
                padding: '8px 14px',
                background: dirty ? '#0ea5e9' : '#d1d5db',
                color: 'white',
                border: 'none',
                borderRadius: 6,
                cursor: dirty ? 'pointer' : 'not-allowed',
                marginRight: 8,
              }}
            >
              {saving ? 'Saving…' : dirty ? 'Save changes' : 'Saved'}
            </button>
          ) : (
            <ReadOnlyBadge style={{ marginRight: 8 }} />
          )}
          <button
            onClick={onReset}
            disabled={!dirty}
            style={{
              padding: '8px 14px',
              background: 'transparent',
              color: dirty ? '#374151' : '#9ca3af',
              border: '1px solid currentColor',
              borderRadius: 6,
              cursor: dirty ? 'pointer' : 'not-allowed',
            }}
          >
            Reset
          </button>
        </div>

        <h3 style={{ fontSize: 15, marginBottom: 8 }}>Anchors</h3>
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {anchorEntries.map(([name]) => (
            <li
              key={name}
              onClick={() => setSelectedAnchor(name)}
              style={{
                padding: '6px 8px',
                cursor: 'pointer',
                background: selectedAnchor === name ? '#e0f2fe' : 'transparent',
                borderRadius: 4,
                marginBottom: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <span
                style={{
                  display: 'inline-block',
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  background: colourFor(name),
                }}
              />
              {name}
            </li>
          ))}
        </ul>

        {selectedAnchor && selectedDef && (
          <div style={{ marginTop: 16, padding: 12, background: '#f9fafb', borderRadius: 6 }}>
            <h3 style={{ fontSize: 15, marginTop: 0 }}>{selectedAnchor}</h3>
            <NumberField
              label="X"
              value={selectedDef.x}
              onChange={(v) => updateAnchor(selectedAnchor, { x: v })}
            />
            <NumberField
              label="Y"
              value={selectedDef.y}
              onChange={(v) => updateAnchor(selectedAnchor, { y: v })}
            />
            <NumberField
              label="Scale"
              step={0.05}
              value={selectedDef.scale ?? 1.0}
              onChange={(v) => updateAnchor(selectedAnchor, { scale: v })}
            />
            <div style={{ marginTop: 8 }}>
              <label style={{ fontSize: 13, color: '#6b7280' }}>Anchor:</label>
              <select
                value={selectedDef.anchor || 'center'}
                onChange={(e) => updateAnchor(selectedAnchor, { anchor: e.target.value })}
                style={{ width: '100%', padding: 4, marginTop: 2 }}
              >
                <option value="center">center</option>
                <option value="top-center">top-center</option>
                <option value="bottom-center">bottom-center</option>
                <option value="top-left">top-left</option>
                <option value="top-right">top-right</option>
                <option value="bottom-left">bottom-left</option>
                <option value="bottom-right">bottom-right</option>
              </select>
            </div>
            <p style={{ fontSize: 13, color: '#6b7280', marginTop: 12, lineHeight: 1.4 }}>
              Drag the element image on the canvas to move it; drag a corner
              handle to resize. The numeric fields above stay in sync.
            </p>
          </div>
        )}

        <h3 style={{ fontSize: 15, marginTop: 24, marginBottom: 8 }}>Preview attrs</h3>
        <SamplePicker
          master={master}
          color={previewColor}
          attrs={previewAttrs}
          onColorChange={setPreviewColor}
          onAttrsChange={setPreviewAttrs}
        />
      </div>

      {/* Centre: SVG canvas */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#f3f4f6',
          borderRadius: 8,
          overflow: 'hidden',
        }}
      >
        <svg
          ref={svgRef}
          viewBox={`0 0 ${cw} ${ch}`}
          style={{ width: '100%', height: '100%', maxHeight: '100%' }}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerLeave={onPointerUp}
        >
          <image href={basePath} x={0} y={0} width={cw} height={ch} />

          {anchorEntries.map(([name, def]) => {
            const value = previewAttrs[name] || defaultAttrsFromMaster(master)[name] || ''
            const url = elementImageUrl(masterId, master, name, value)
            const dims = imgDims[name]
            const scale = def.scale ?? 1.0
            const w = dims ? dims.w * scale : 200
            const h = dims ? dims.h * scale : 200
            const [ox, oy] = anchorOffset(def.anchor, w, h)
            const x = def.x - ox
            const y = def.y - oy
            const isSelected = selectedAnchor === name
            const colour = colourFor(name)
            return (
              <g key={name}>
                {/* Hidden raster to measure intrinsic dims. Falls back
                    to a small probe rect when no element image is
                    available (e.g. master declares an attr but no
                    matching value). */}
                {url && (
                  <image
                    href={url}
                    x={x}
                    y={y}
                    width={w}
                    height={h}
                    style={{ cursor: 'grab', touchAction: 'none', opacity: 0.92 }}
                    onPointerDown={(e) => onElementPointerDown(name, 'move', e)}
                    onClick={() => setSelectedAnchor(name)}
                    onLoad={(e) => {
                      const img = e.currentTarget as unknown as SVGImageElement & {
                        naturalWidth?: number
                        naturalHeight?: number
                      }
                      // SVG <image> exposes naturalWidth/naturalHeight in
                      // modern browsers. Fall back to the rendered size.
                      const nw = img.naturalWidth || w
                      const nh = img.naturalHeight || h
                      setImgDims((prev) => {
                        const cur = prev[name]
                        if (cur && cur.w === nw && cur.h === nh) return prev
                        return { ...prev, [name]: { w: nw, h: nh } }
                      })
                    }}
                  />
                )}

                {/* Selection chrome — bounding box + corner handles +
                    centre dot + label. Always rendered on top. */}
                {isSelected && (
                  <>
                    <rect
                      x={x}
                      y={y}
                      width={w}
                      height={h}
                      fill="none"
                      stroke={colour}
                      strokeWidth={3}
                      strokeDasharray="8 6"
                      pointerEvents="none"
                    />
                    {/* Corner resize handles. Each one drives scale via
                        distance-from-anchor ratio. */}
                    {([
                      ['resize-tl', x, y],
                      ['resize-tr', x + w, y],
                      ['resize-bl', x, y + h],
                      ['resize-br', x + w, y + h],
                    ] as Array<[DragMode, number, number]>).map(([mode, hx, hy]) => (
                      <rect
                        key={mode}
                        x={hx - 14}
                        y={hy - 14}
                        width={28}
                        height={28}
                        fill="white"
                        stroke={colour}
                        strokeWidth={3}
                        rx={4}
                        ry={4}
                        style={{ cursor: 'nwse-resize', touchAction: 'none' }}
                        onPointerDown={(e) => onElementPointerDown(name, mode, e)}
                      />
                    ))}
                  </>
                )}

                {/* Anchor dot — kept so operators always have a clear
                    grab target at the actual anchor coordinate, even
                    when the element image is huge or has transparent
                    edges that don't catch pointer events. */}
                <g
                  transform={`translate(${def.x}, ${def.y})`}
                  style={{ cursor: 'grab', touchAction: 'none' }}
                  onPointerDown={(e) => onElementPointerDown(name, 'move', e)}
                  onClick={() => setSelectedAnchor(name)}
                >
                  <circle
                    r={isSelected ? 18 : 12}
                    fill={colour}
                    opacity={0.9}
                    stroke="white"
                    strokeWidth={3}
                  />
                  <text
                    x={24}
                    y={6}
                    style={{
                      fontFamily: 'system-ui, sans-serif',
                      fontSize: 23,
                      fontWeight: 600,
                      fill: '#111827',
                      paintOrder: 'stroke',
                      stroke: 'white',
                      strokeWidth: 4,
                    }}
                    pointerEvents="none"
                  >
                    {name}
                  </text>
                </g>
              </g>
            )
          })}
        </svg>
      </div>

      {/* Right: live preview pane */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h3 style={{ margin: 0, fontSize: 15 }}>Live preview</h3>
          {previewBusy && <span style={{ fontSize: 13, color: '#6b7280' }}>Rendering…</span>}
        </div>
        <div
          style={{
            background: '#fff',
            borderRadius: 8,
            border: '1px solid #e5e7eb',
            overflow: 'hidden',
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 8,
          }}
        >
          {previewUrl ? (
            <img src={previewUrl} alt="preview" style={{ maxWidth: '100%', maxHeight: '100%' }} />
          ) : (
            <span style={{ color: '#9ca3af', fontSize: 14 }}>Waiting for first render…</span>
          )}
        </div>
      </div>
    </div>
  )
}


function NumberField({
  label,
  value,
  onChange,
  step = 1,
}: {
  label: string
  value: number
  onChange: (v: number) => void
  step?: number
}) {
  return (
    <div style={{ marginBottom: 6 }}>
      <label style={{ fontSize: 13, color: '#6b7280' }}>{label}:</label>
      <input
        type="number"
        value={Number.isFinite(value) ? value : 0}
        step={step}
        onChange={(e) => {
          const v = parseFloat(e.target.value)
          if (Number.isFinite(v)) onChange(v)
        }}
        style={{ width: '100%', padding: 4, marginTop: 2 }}
      />
    </div>
  )
}


function SamplePicker({
  master,
  color,
  attrs,
  onColorChange,
  onAttrsChange,
}: {
  master: StyleMaster
  color: string
  attrs: Record<string, string>
  onColorChange: (c: string) => void
  onAttrsChange: (a: Record<string, string>) => void
}) {
  // Pull colour options from the master's swatch picker if it declares
  // by_color; otherwise fall back to a sane default palette.
  const colourOpts = useMemo(() => {
    const fromMaster = Object.keys(master.fabric_swatch_picker?.by_color || {})
    if (fromMaster.length > 0) return fromMaster
    return ['white', 'blue', 'navy', 'pink', 'lilac']
  }, [master])

  return (
    <div style={{ padding: 12, background: '#f9fafb', borderRadius: 6 }}>
      <SelectField
        label="Color"
        value={color}
        options={colourOpts}
        onChange={onColorChange}
      />
      {Object.keys(master.elements || {}).map((attr) => {
        const opts = Object.keys(master.elements[attr] || {})
        return (
          <SelectField
            key={attr}
            label={attr}
            value={attrs[attr] || opts[0] || ''}
            options={opts}
            onChange={(v) => onAttrsChange({ ...attrs, [attr]: v })}
          />
        )
      })}
    </div>
  )
}


function SelectField({
  label,
  value,
  options,
  onChange,
}: {
  label: string
  value: string
  options: string[]
  onChange: (v: string) => void
}) {
  return (
    <div style={{ marginBottom: 6 }}>
      <label style={{ fontSize: 13, color: '#6b7280' }}>{label}:</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{ width: '100%', padding: 4, marginTop: 2 }}
      >
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </div>
  )
}
